import React, { useEffect, useMemo, useState } from 'react';
import { useTerms } from '../../../layout';
import { Alert, Button, Drawer, Field, Input } from '../../../components/shared';
import adminAPI, { errorMessage } from '../../../services/adminApi';
import { callsApi } from '../../../services/callsApi';
import { ENTRY_TYPES, setCommunityBody, sortEntries } from '../../admin/directoryModel';

/** Set or clear a call's community by hand (mockup 13-call-detail.html#set-community), optionally saving the caller's number to the directory. */
export function SetCommunityDrawer({ open, call, callerName, callerUnit, onClose, onSaved }) {
  const terms = useTerms();
  const [lookups, setLookups] = useState({ communities: null, entries: null });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const hasNumber = Boolean(call?.callerNumber);

  useEffect(() => {
    if (!open) return;
    setProblem(null);
    setForm({
      communityId: call.communityId || '',
      saveNumber: hasNumber && !call.callerDirectory,
      mode: 'new',
      entryId: '',
      fullName: callerName || '',
      entryType: 'Homeowner',
      unitOrLot: callerUnit || '',
    });
    let cancelled = false;
    adminAPI
      .getCommunities()
      .then((communities) => !cancelled && setLookups((l) => ({ ...l, communities })))
      .catch((err) => !cancelled && setProblem(errorMessage(err, `${terms.communities} couldn't load. Close this and try again.`)));
    return () => {
      cancelled = true;
    };
  }, [open, call, callerName, callerUnit, hasNumber, terms.communities]);

  useEffect(() => {
    if (!open || form?.mode !== 'existing' || lookups.entries) return;
    let cancelled = false;
    adminAPI
      .getDirectory()
      .then((entries) => !cancelled && setLookups((l) => ({ ...l, entries: sortEntries(entries).filter((e) => e.isActive !== false) })))
      .catch((err) => !cancelled && setProblem(errorMessage(err, "The directory couldn't load.")));
    return () => {
      cancelled = true;
    };
  }, [open, form?.mode, lookups.entries]);

  const communities = useMemo(() => (lookups.communities || []).filter((c) => c.isActive !== false || c.id === call?.communityId).sort((a, b) => a.name.localeCompare(b.name)), [lookups.communities, call]);
  const people = useMemo(() => (lookups.entries || []).filter((e) => !form?.communityId || !e.communityId || e.communityId === form.communityId), [lookups.entries, form?.communityId]);

  if (!open || !form) return null;
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const chosen = communities.find((c) => c.id === form.communityId);
  const invalid = form.saveNumber && (form.mode === 'existing' ? !form.entryId : !form.fullName.trim());

  const submit = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const updated = await callsApi.setCommunity(call.id, setCommunityBody(form));
      const name = chosen ? chosen.name : `no ${terms.communityLower}`;
      await onSaved(updated, form.saveNumber ? `${terms.community} set to ${name}; number saved to the directory` : `${terms.community} set to ${name}`);
    } catch (err) {
      setProblem(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={`Set ${terms.communityLower}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={busy || !lookups.communities || invalid}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label={terms.community} htmlFor="sc-c" hint="Set by hand. Syncing and directory changes never overwrite it.">
          <select className="select" id="sc-c" value={form.communityId} onChange={set('communityId')} disabled={!lookups.communities}>
            {communities.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
            <option value="">No {terms.communityLower}</option>
          </select>
        </Field>
        {hasNumber && (
          <>
            <div className="panel small">
              <div className="strong">{call.callerNumber}</div>
              <div className="caption">Caller ID on this call</div>
            </div>
            <label className="check">
              <input type="checkbox" id="sc-save" checked={form.saveNumber} onChange={set('saveNumber')} />
              Save this number to the directory
            </label>
            {form.saveNumber && (
              <div className="stack" style={{ gap: 10, paddingLeft: 24 }}>
                <div className="row" style={{ gap: 16 }}>
                  <label className="check">
                    <input type="radio" name="sc-m" value="new" checked={form.mode === 'new'} onChange={set('mode')} />
                    New person
                  </label>
                  <label className="check">
                    <input type="radio" name="sc-m" value="existing" checked={form.mode === 'existing'} onChange={set('mode')} />
                    Add to an existing person
                  </label>
                </div>
                {form.mode === 'new' ? (
                  <>
                    <Field label="Name" htmlFor="sc-n">
                      <Input id="sc-n" value={form.fullName} onChange={set('fullName')} />
                    </Field>
                    <div className="form-grid">
                      <Field label="Type" htmlFor="sc-t">
                        <select className="select" id="sc-t" value={form.entryType} onChange={set('entryType')}>
                          {ENTRY_TYPES.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Unit or lot" htmlFor="sc-l">
                        <Input id="sc-l" value={form.unitOrLot} onChange={set('unitOrLot')} />
                      </Field>
                    </div>
                  </>
                ) : (
                  <Field label="Person" htmlFor="sc-p">
                    <select className="select" id="sc-p" value={form.entryId} onChange={set('entryId')} disabled={!lookups.entries}>
                      <option value="">{lookups.entries ? (people.length ? 'Choose a person…' : `No one listed for this ${terms.communityLower}`) : 'Loading the directory…'}</option>
                      {people.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.unitOrLot ? `${e.fullName} · ${e.unitOrLot}` : e.fullName}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
                <span className="hint">
                  Later calls from this number go to {chosen ? chosen.name : `the person's ${terms.communityLower}`}. Earlier calls from it are updated too, unless their {terms.communityLower} was set by hand.
                </span>
              </div>
            )}
          </>
        )}
        {problem && <Alert tone="error">{problem}</Alert>}
      </div>
    </Drawer>
  );
}
